import type React from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { Button } from '@root/components'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from '@root/hooks'
import { useBusinessContext } from '@root/context'
import { FaTimes } from 'react-icons/fa'
import { FaArrowRight, FaArrowTrendUp } from 'react-icons/fa6'
import { useQuery } from '@tanstack/react-query'
import { DashboardAlertTypes, dashboardQueryKey, fetchAlerts } from '@root/query'
import { NocfoPlan, getSettingsPageUrl } from '@root/constants'

type DismissValue = 'dismissed' | undefined

const PlanRecommendationBanner: React.FC = () => {
  const { slug, data: business } = useBusinessContext()
  const { t } = useTranslation()

  const [isDismissed, setDismissed] = useLocalStorage<DismissValue>(
    `${slug}.planRecommendation`,
    undefined
  )

  const { data: alerts } = useQuery({
    queryKey: dashboardQueryKey(slug, 'alerts'),
    queryFn: () => fetchAlerts({ slug }),
    staleTime: 5 * 60 * 1000
  })

  const recommendation = alerts?.find(
    alert => alert.type === DashboardAlertTypes.PLAN_RECOMMENDATION
  )

  if (!recommendation) return null
  if (isDismissed === 'dismissed') return null
  if (business?.subscription_plan !== NocfoPlan.FREE) return null

  return (
    <Container>
      <IconContainer>
        <FaArrowTrendUp />
      </IconContainer>
      <ContentContainer>
        <Title>{recommendation.title}</Title>
        <Message>{recommendation.message}</Message>
        <StyledLink to={getSettingsPageUrl(slug, { page: 'plan' })}>
          <StyledButton iconRight={<FaArrowRight />} size="sm">
            {t('document.planRecommendation.viewPlans')}
          </StyledButton>
        </StyledLink>
      </ContentContainer>
      <DismissButton
        type="button"
        aria-label={t('common.dismiss')}
        onClick={() => setDismissed('dismissed')}
      >
        <FaTimes />
      </DismissButton>
    </Container>
  )
}

export default PlanRecommendationBanner

const StyledLink = styled(Link)`
  display: inline-block;
  width: fit-content;
  height: fit-content;
`

const StyledButton = styled(Button)`
  background: ${({ theme }) => theme.colors.primary} !important;
  color: ${({ theme }) => theme.colors.neutralWhite} !important;
`

const Title = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.xxs}rem 0;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Message = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm}rem 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const IconContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.primary};

  & > * {
    width: 1.2rem;
    height: 1.2rem;
  }
`

const ContentContainer = styled.div`
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  padding-bottom: ${({ theme }) => theme.spacing.md}rem;
  padding-right: ${({ theme }) => theme.spacing.xl}rem;
  flex: 1;
  overflow: auto;
`

const Container = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 }
})`
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  position: relative;
  overflow: auto;
`

const DismissButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm}rem;
  right: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme }) => theme.colors.iconGrayDark};
  outline: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
`
